import React, { useState } from 'react'
import { MenuItem } from '@mui/material'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import '../../i18n'
import NumberInput from '../../components/common/NumberInput'
import UseAlert from '../../components/common/useAlerte'
import { useNavigate, useParams } from 'react-router-dom'

import { addExistingControl } from '../../redux/existingControls/existingControlActions'

import SelectcontrolNature from '../../components/common/ControlNature'
import { IncidentPerimeter } from '../../types/existingControlTypes'
import {
  FormAside,
  FormField,
  FormFoot,
  FormGrid,
  FormLayout,
  FormPageHead,
  FormSection,
  FormShell,
} from '../../components/common/form'

const ExistingControlCreate = () => {
  const { id } = useParams<{ id: string }>()

  const [existingControl, setExistingControl] = useState({
    Label: '',
    Description: '',
    Perimetre: 'NETWORK',
    NombreActifCouvert: 0,
    TauxCouverture: 0,
    ControlNature: 'Autres',
    SecurityControlId: id || '',
  })

  const dispatch: AppDispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const handleInputChange = (event: any) => {
    const { name, value } = event.target
    setExistingControl((prevExistingControl) => ({
      ...prevExistingControl,
      [name]: value,
    }))
  }
  const handleCloseAlert = () => {
    setAlert({
      ...alert,
      open: false,
    })

    if (alert.status === 200) {
      navigate(`/security-existing/${id}`)
    }
  }

  const status = useSelector(
    (state: RootState) => state.existingControl?.addedExistingControl?.status
  )
  const message = useSelector(
    (state: RootState) => state.existingControl.addedExistingControl?.message
  )

  const [alert, setAlert] = useState({
    open: false,
    message: '',
    status: 0,
  })

  const handleAddExistingControl = async () => {
    if (existingControl.Label && id) {
      const newexistingControl = {
        Label: existingControl.Label,
        Description: existingControl.Description,
        Perimetre: existingControl.Perimetre,
        NombreActifCouvert: existingControl.NombreActifCouvert,
        TauxCouverture: existingControl.TauxCouverture,
        ControlNature: existingControl.ControlNature,
        SecurityControlId: id,
      }
      await dispatch(addExistingControl(newexistingControl))
      setExistingControl({
        Label: '',
        Description: '',
        Perimetre: '',
        NombreActifCouvert: 0,
        TauxCouverture: 0,
        ControlNature: 'Autres',
        SecurityControlId: id || '',
      })
      setAlert({
        open: true,
        message: message || t('alerts.existingControl.success'),
        status: status || 200,
      })
    } else {
      setAlert({
        open: true,
        message: t('alerts.existingControl.emptyFields'),
        status: 0,
      })
    }
  }
  const handlePerimetreChange = (event: SelectChangeEvent<string>) => {
    setExistingControl((prev) => ({
      ...prev,
      Perimetre: event.target.value,
    }))
  }
  const submitLabel = t('buttons.add')
  const backPath = `/security-existing/${id}`

  return (
    <div className="page-content">
      <FormPageHead
        eyebrow={t('Existing Controls')}
        title={t('buttons.addExistingControl')}
        subtitle={t('existingControlForm.createSubtitle', {
          defaultValue:
            'Document the scope, nature and coverage of an existing control.',
        })}
        backLabel={t('Back', { defaultValue: 'Back to controls' })}
        onBack={() => navigate(backPath)}
      />
      <UseAlert
        open={alert.open}
        message={alert.message}
        status={alert.status}
        onClose={handleCloseAlert}
      />
      <FormLayout
        main={
          <FormShell>
            <FormSection title={t('Control details', { defaultValue: 'Control details' })}>
              <FormGrid cols={2}>
                <FormField label={t('label')} required>
                  <input className="incyia-form-inp" name="Label" value={existingControl.Label} onChange={handleInputChange} />
                </FormField>
                <FormField label={t('Perimeter')} required>
                  <Select className="incyia-form-inp" value={existingControl.Perimetre} onChange={handlePerimetreChange}>
                    <MenuItem value="" disabled />
                    {Object.entries(IncidentPerimeter).map(([key, value]) => (
                      <MenuItem key={key} value={key}>{value}</MenuItem>
                    ))}
                  </Select>
                </FormField>
                <FormField label={t('Description')} required span>
                  <textarea className="incyia-form-inp" name="Description" value={existingControl.Description} onChange={handleInputChange} rows={4} />
                </FormField>
              </FormGrid>
            </FormSection>
            <FormSection title={t('Coverage', { defaultValue: 'Coverage' })}>
              <FormGrid cols={3}>
                <FormField label={t('Control Nature')}>
                  <SelectcontrolNature value={existingControl.ControlNature} onChange={handleInputChange} />
                </FormField>
                <FormField label={t('Number of Active Covered')}>
                  <NumberInput decimals={0} min={0} name="NombreActifCouvert" value={existingControl.NombreActifCouvert} onChange={(v) => setExistingControl((prev) => ({ ...prev, NombreActifCouvert: v ?? 0 }))} />
                </FormField>
                <FormField label={t('Coverage Rate')}>
                  <NumberInput min={0} max={100} adornment="%" name="TauxCouverture" value={existingControl.TauxCouverture} onChange={(v) => setExistingControl((prev) => ({ ...prev, TauxCouverture: v ?? 0 }))} />
                </FormField>
              </FormGrid>
            </FormSection>
            <FormFoot
              note={t('actionCreate.aside.requiredMark', { defaultValue: '* Required' })}
              cancelLabel={t('Cancel')}
              submitLabel={submitLabel}
              onCancel={() => navigate(backPath)}
              onSubmit={handleAddExistingControl}
            />
          </FormShell>
        }
        aside={
          <FormAside
            title={t('actionCreate.aside.inThisForm', { defaultValue: 'In this form' })}
            steps={[
              { title: t('Control details', { defaultValue: 'Control details' }), fieldCount: 3 },
              { title: t('Coverage', { defaultValue: 'Coverage' }), fieldCount: 3 },
            ]}
            requiredCount={2}
            submitLabel={submitLabel}
            tipTitle={t('actionCreate.aside.goodToKnow', { defaultValue: 'Good to know' })}
            tip={t('existingControlForm.tip', { defaultValue: 'Coverage information helps assess how completely this control protects the selected scope.' })}
            fieldsLabel={(n) => `${n} ${n === 1 ? 'field' : 'fields'}`}
            requiredNote={(n, submit) => `${n} required before you can ${submit}.`}
          />
        }
      />
    </div>
  )
}

export default ExistingControlCreate
